import {sendEvent} from '@github-ui/hydro-analytics'
import {loaded} from '@github-ui/document-ready'
import type {
  CLSMetricWithAttribution,
  FCPMetricWithAttribution,
  FIDMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
  TTFBMetricWithAttribution,
} from 'web-vitals/attribution'
import type {HPCTimingEvent} from './hpc/timing-event'

type WebVitalMetric =
  | CLSMetricWithAttribution
  | FCPMetricWithAttribution
  | FIDMetricWithAttribution
  | INPMetricWithAttribution
  | LCPMetricWithAttribution
  | TTFBMetricWithAttribution

interface WebVitalInformation {
  name: string
  value: number
  element?: string
}

interface HPCInformation extends WebVitalInformation {
  mechanism: HPCTimingEvent['mechanism']
  soft: boolean
}

interface HydroStat {
  react?: boolean
  reactApp?: string | null
  reactPartials?: string[]
  ssr?: boolean
  HPC?: HPCInformation
  TTFB?: WebVitalInformation
  FCP?: WebVitalInformation
  LCP?: WebVitalInformation
  FID?: WebVitalInformation
  INP?: WebVitalInformation
  CLS?: WebVitalInformation
}

let queued: HydroStat | undefined

/**
 * Batched report of vital to hydro
 */
export function sendStatToHydro(metric: WebVitalMetric | HPCTimingEvent, ssr: boolean) {
  let hydroStat: HydroStat | undefined

  if (!document.querySelector('[data-hydrostats="publish"]')) return

  if (metric.value < 60_000) {
    if (!hydroStat) {
      const reactApp = document.querySelector('react-app')
      hydroStat = queueStat()
      hydroStat.react = !!reactApp
      hydroStat.reactApp = reactApp?.getAttribute('app-name')
      // Convert to Set and back to Array to remove duplicates.
      hydroStat.reactPartials = [
        ...new Set(
          Array.from(document.querySelectorAll('react-partial')).map(
            partial => partial.getAttribute('partial-name') || '',
          ),
        ),
      ]
      hydroStat.ssr = ssr
    }

    if (metric.name === 'HPC') {
      hydroStat[metric.name] = buildHPCInformation(metric)
    } else {
      hydroStat[metric.name] = buildWebVitalInformation(metric)
    }
  }
}

function buildHPCInformation(metric: HPCTimingEvent): HPCInformation {
  return {
    name: metric.name,
    value: metric.value,
    element: metric.attribution?.element,
    soft: !!metric.soft,
    mechanism: metric.mechanism,
  }
}

function buildWebVitalInformation(metric: WebVitalMetric): WebVitalInformation {
  const vitalInformation: WebVitalInformation = {
    name: metric.name,
    value: metric.value,
  }

  switch (metric.name) {
    case 'LCP':
      vitalInformation.element = metric.attribution?.element
      break
    case 'FID':
    case 'INP':
      vitalInformation.element = metric.attribution?.eventTarget
      break
    case 'CLS':
      vitalInformation.element = metric.attribution?.largestShiftTarget
      break
  }

  return vitalInformation
}

/**
 * Create a new stat object and schedule it to be sent to hydro
 */
function queueStat(): HydroStat {
  if (!queued) {
    queued = {}
    scheduleSend()
  }
  return queued
}

/**
 * Schedule a send to hydro
 */
async function scheduleSend() {
  await loaded
  // eslint-disable-next-line compat/compat
  window.requestIdleCallback(send)
}

/**
 * Send the queued event to hydro
 */
function send() {
  sendEvent('web-vital', queued as Record<string, string | number | boolean | null | undefined>)
  queued = undefined
}
